import { request, requestJSON } from '@codametrix/client-api';
import { documentUtils } from '@codametrix/ui-common';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  CmxDocumentContent,
  CmxDocumentContentStatus
} from './cmx-document-content-types';

/**
 * The layer a coder reads to see the document body.
 *
 * LAYER 2, not 3, and the reason is worth stating because the ticket wording
 * disagrees. The FE stories say "L3" for markdown-without-sections, but the
 * spec's L3 is a tier-3 body carrying *recovered* structure, which this
 * increment does not build. What the stories want is a body served with no
 * annotation payload, which is layer 2. A request for layer 3 fails in this
 * increment (CDMX-20642 AC 5 puts it out of scope) -- note that is a scope
 * decision, not a schema rule: the contract accepts any layer 1-5 and returns
 * 400 only for a numeral outside that range. So this constant is expected to
 * change without the contract changing.
 *
 * A coder span must never anchor to layer 1: the flat form is derived from
 * whatever the current body is and its bytes move when the body is re-tiered
 * (CMXDC-LAYER-010). Layer 2 and above are stable anchors.
 */
export const CMX_DOCUMENT_CONTENT_BODY_LAYER = 2;

/** The layer carrying generic section boundaries. */
export const CMX_DOCUMENT_CONTENT_SECTION_LAYER = 4;

export function cmxDocumentContentLayerPath(
  documentId: string,
  layer: number
): string {
  // Bare path under `/documents`, matching where documentation-service's
  // CmxDocumentContentController actually mounts (`/documents/cmx-document-content/...`),
  // consistent with the existing documentation-service calls (`/documents/v1`,
  // `/documents/v2`). client-api's path resolver rewrites it to the BFF prefix
  // when USE_API_PROXY_ROUTES is on, so this endpoint has to be exposed through
  // the BFF/api-proxy -- documentation-service is not in the resolver's
  // UNCHANGED_PREFIXES list the way cqa-service is.
  return `/documents/cmx-document-content/${encodeURIComponent(documentId)}/layers/${layer}/v1`;
}

export function cmxDocumentContentStatusPath(documentId: string): string {
  return `/documents/cmx-document-content/${encodeURIComponent(documentId)}/v1`;
}

export function cmxDocumentContentSourcePath(documentId: string): string {
  return `/documents/cmx-document-content/${encodeURIComponent(documentId)}/source/v1`;
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export type CmxDocumentContentSource = {
  mediaType: string;
  content: string;
};

export type CmxDocumentContentSourceState = {
  source: CmxDocumentContentSource | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
};

export function useCmxDocumentContentSource(
  documentId: string | null | undefined
): CmxDocumentContentSourceState {
  const [source, setSource] = useState<CmxDocumentContentSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const currentRequest = useRef(0);

  const load = useCallback(() => {
    if (!documentId) {
      setSource(null);
      setError(null);
      setLoading(false);
      return;
    }

    currentRequest.current += 1;
    const requestId = currentRequest.current;
    const isCurrent = () => currentRequest.current === requestId;

    setLoading(true);
    setError(null);

    request(cmxDocumentContentSourcePath(documentId))
      .then(async (response) => {
        const mediaType =
          response.headers.get('Content-Type')?.split(';')[0]?.trim() ??
          'application/octet-stream';
        const content =
          mediaType === documentUtils.MimeTypes.PDF
            ? arrayBufferToBase64(await response.arrayBuffer())
            : await response.text();

        if (isCurrent()) {
          setSource({ mediaType, content });
        }
      })
      .catch((caught: unknown) => {
        if (isCurrent()) {
          setSource(null);
          setError(
            caught instanceof Error ? caught : new Error(String(caught))
          );
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setLoading(false);
        }
      });
  }, [documentId]);

  useEffect(() => {
    load();

    return () => {
      currentRequest.current += 1;
    };
  }, [load]);

  return { source, loading, error, reload: load };
}

/**
 * The highest layer that is actually retrievable, or `null` if none is.
 *
 * `ready` only. `pending` and `notRun` are both "not now" and `failed` is "not ever", and none of
 * the three can be read -- asking for one is a 404 that looks exactly like a document that does
 * not exist. Highest rather than a fixed numeral because layers are cumulative in richness: a
 * ready layer 4 carries sections a ready layer 2 does not, so the best available body is the top
 * of the ready set.
 *
 * Note layer 1 is deliberately eligible. It is the flat form, viewable but not a legal anchor for
 * an annotation (`CMXDC-LAYER-010`: its bytes move when the body is re-tiered), so a caller that
 * intends to WRITE spans must not use this alone -- read-only rendering is what it is for.
 */
export function highestReadyLayer(
  status: CmxDocumentContentStatus | null | undefined
): number | null {
  const ready = (status?.layers ?? []).filter(
    (entry) => entry.state === 'ready'
  );
  return ready.length === 0
    ? null
    : ready.reduce((best, entry) => Math.max(best, entry.layer), 0);
}

export type CmxDocumentContentStatusState = {
  status: CmxDocumentContentStatus | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
};

/**
 * Reads a CMX Document Content's status -- which layers exist and which are retrievable.
 *
 * Separate hook rather than folded into `useCmxDocumentContent` because the two answer different
 * questions and a caller may want either alone: status distinguishes "not yet" from "never"
 * without transferring a body, and a caller that already knows its layer should not pay for a
 * status round trip.
 */
export function useCmxDocumentContentStatus(
  documentId: string | null | undefined
): CmxDocumentContentStatusState {
  const [status, setStatus] = useState<CmxDocumentContentStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const currentRequest = useRef(0);

  const load = useCallback(() => {
    if (!documentId) {
      setStatus(null);
      setError(null);
      setLoading(false);
      return;
    }

    currentRequest.current += 1;
    const requestId = currentRequest.current;
    const isCurrent = () => currentRequest.current === requestId;

    setLoading(true);
    setError(null);

    requestJSON<CmxDocumentContentStatus>(cmxDocumentContentStatusPath(documentId))
      .then((result) => {
        if (isCurrent()) {
          setStatus(result);
        }
      })
      .catch((caught: unknown) => {
        if (isCurrent()) {
          setStatus(null);
          setError(
            caught instanceof Error ? caught : new Error(String(caught))
          );
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setLoading(false);
        }
      });
  }, [documentId]);

  useEffect(() => {
    load();

    return () => {
      currentRequest.current += 1;
    };
  }, [load]);

  return { status, loading, error, reload: load };
}

export type CmxDocumentContentState = {
  document: CmxDocumentContent | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
};

/**
 * Reads one layer of a CMX Document Content.
 *
 * Deliberately a plain hook rather than a createAsyncThunk: the body is
 * read-only and needed by one view, so there is nothing for the store to
 * coordinate. The cqa pattern in ui-common's grade-bench-panel-actions is the
 * model if this ever needs to be shared across components.
 */
export function useCmxDocumentContent(
  documentId: string | null | undefined,
  layer: number = CMX_DOCUMENT_CONTENT_BODY_LAYER
): CmxDocumentContentState {
  const [document, setDocument] = useState<CmxDocumentContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Guards against a slow response for a previous documentId overwriting a
  // newer one -- the coder can change cases faster than a fetch resolves, and
  // a stale body under a new case is worse than a slow one.
  const currentRequest = useRef(0);

  const load = useCallback(() => {
    if (!documentId) {
      setDocument(null);
      setError(null);
      setLoading(false);
      return;
    }

    currentRequest.current += 1;
    const requestId = currentRequest.current;
    const isCurrent = () => currentRequest.current === requestId;

    setLoading(true);
    setError(null);

    requestJSON<CmxDocumentContent>(cmxDocumentContentLayerPath(documentId, layer))
      .then((result) => {
        if (isCurrent()) {
          setDocument(result);
        }
      })
      .catch((caught: unknown) => {
        if (isCurrent()) {
          setDocument(null);
          setError(
            caught instanceof Error ? caught : new Error(String(caught))
          );
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setLoading(false);
        }
      });
  }, [documentId, layer]);

  useEffect(() => {
    load();

    // Invalidate anything in flight on unmount, so a late response cannot
    // set state on a torn-down component.
    return () => {
      currentRequest.current += 1;
    };
  }, [load]);

  return { document, loading, error, reload: load };
}
